import React, { useMemo } from 'react';
import BatchImportModal from '../components/BatchImportModal';
import MergeDialog from '../components/MergeDialog';
import { PatientFormModal } from '../components/patient/PatientFormModal';
import { PatientBulkUploadDialog } from '../components/patient/PatientBulkUploadDialog';
import { EligibilityResultDialog } from '../components/patient/EligibilityResultDialog';
import NeedsCompletionBanner from '../components/shared/NeedsCompletionBanner';
import { PatientsBulkActionBar } from './patients/PatientsBulkActionBar';
import { PatientsDuplicatesPanel } from './patients/PatientsDuplicatesPanel';
import { PatientsFilters } from './patients/PatientsFilters';
import { PatientsPageHeader } from './patients/PatientsPageHeader';
import { PatientsTable } from './patients/PatientsTable';
import { usePatients } from './patients/usePatients';
import { useEligibilityAutomation } from './patients/useEligibilityAutomation';
import { EligibilityAutomationDialog } from './patients/EligibilityAutomationDialog';
import { useAuth } from '../context/AuthContext';

/**
 * Patients page — thin orchestrator composing the header, filters, table,
 * and dialogs. All state lives in `usePatients`. See `pages/patients/`
 * for the individual presentational components.
 */
const PatientsPage = () => {
  const c = usePatients();
  const { user } = useAuth();
  const canManageAutomation = user?.role === 'Admin'
    || (user?.entity_type === 'azai' && ['super_admin', 'admin'].includes(user?.azai_role));
  const automation = useEligibilityAutomation({
    selectedIds: c.selectedIds,
    onDone: () => { c.setSelectedIds([]); c.fetchPatients(); },
  });
  const needsCompletionCount = useMemo(
    () => (c.patients || []).filter((p) => p.status === 'Needs completion').length,
    [c.patients]
  );

  return (
    <div className="space-y-6" data-testid="patients-page">
      <PatientsPageHeader
        scanningDuplicates={c.scanningDuplicates}
        onScanDuplicates={c.handleScanDuplicates}
        onImport={() => c.setImportModalOpen(true)}
        onBulkUpload={() => c.setBulkUploadOpen(true)}
        onAddPatient={() => c.handleOpenModal()}
      />

      <NeedsCompletionBanner
        count={needsCompletionCount}
        recordLabel="patient"
        onClickComplete={() => c.setFilterStatus('Needs completion')}
      />

      <PatientsBulkActionBar
        selectedCount={c.selectedIds.length}
        onClear={() => c.setSelectedIds([])}
        onBulkDelete={c.handleBulkDelete}
        bulkDeleting={c.bulkDeleting}
        canManageAutomation={canManageAutomation}
        onEnrollAutomation={() => automation.open(true)}
        onUnenrollAutomation={() => automation.open(false)}
      />
      <EligibilityAutomationDialog auto={automation} selectedCount={c.selectedIds.length} />

      <PatientsDuplicatesPanel
        duplicates={c.duplicates}
        show={c.showDuplicates}
        onDismiss={() => c.setShowDuplicates(false)}
        onMerge={c.setMergeGroup}
      />

      <PatientsFilters
        search={c.search}
        onSuggestSelect={c.handleSuggestSelect}
        filterPayer={c.filterPayer}
        onFilterPayer={c.setFilterPayer}
        filterStatus={c.filterStatus}
        onFilterStatus={c.setFilterStatus}
      />

      <PatientsTable
        patients={c.patients}
        loading={c.loading}
        selectedIds={c.selectedIds}
        isAllSelected={c.isAllSelected}
        isSomeSelected={c.isSomeSelected}
        onToggleSelectAll={c.toggleSelectAll}
        onToggleSelect={c.toggleSelect}
        onAddFirst={() => c.handleOpenModal()}
        onEdit={c.handleOpenModal}
        onDelete={c.handleDelete}
        onCheckEligibility={c.handleEligibilityCheck}
        onToggleStatus={c.handleToggleStatus}
        checkingEligibility={c.checkingEligibility}
      />

      <PatientFormModal
        isOpen={c.isModalOpen}
        onClose={c.handleCloseModal}
        onSubmit={c.handleSubmit}
        saving={c.saving}
        editingPatient={c.editingPatient}
        formData={c.formData}
        handleChange={c.handleChange}
        npiSearchResults={c.form.npiSearchResults}
        npiSearching={c.form.npiSearching}
        showNpiResults={c.form.showNpiResults}
        handleNpiSearch={c.form.handleNpiSearch}
        handleSelectNpi={c.form.handleSelectNpi}
        initialTab={c.initialTab}
        focusField={c.focusField}
      />

      <BatchImportModal
        isOpen={c.importModalOpen}
        onClose={() => c.setImportModalOpen(false)}
        importType="patient"
        onImportComplete={c.fetchPatients}
      />

      <PatientBulkUploadDialog
        isOpen={c.bulkUploadOpen || false}
        onClose={() => c.setBulkUploadOpen?.(false)}
        onImportComplete={c.fetchPatients}
      />

      <MergeDialog
        isOpen={!!c.mergeGroup}
        onClose={() => c.setMergeGroup(null)}
        mergeType="patient"
        group={c.mergeGroup}
        onMergeComplete={() => { c.fetchPatients(); c.handleScanDuplicates(); }}
      />

      <EligibilityResultDialog
        open={!!c.eligibilityResult}
        onClose={c.handleCloseEligibility}
        result={c.eligibilityResult}
        patient={c.eligibilityPatient}
        onRecheck={c.handleRecheckEligibility}
        rechecking={c.checkingEligibility}
      />

      {c.ConfirmDialog}
    </div>
  );
};

export default PatientsPage;
